import { Task, TaskPriority } from '../types';
import { EnhancedTaskAnalyzer, TaskType, EnhancedAnalysisResult } from './enhanced-task-analyzer';
import { logger } from '../utils/logger';

export interface SubTask {
  id: string;
  title: string;
  description?: string;
  estimatedTime: number; // minutes
  dependencies?: string[]; // IDs of tasks that must be completed first
  type: TaskType;
  priority: TaskPriority;
  parent?: string;
  children?: SubTask[];
}

export interface SplitOptions {
  maxHours?: number; // Maximum hours per task (default: 4)
  maxDepth?: number; // Maximum nesting depth (default: 3)
  includeTests?: boolean; // Include test tasks (default: true)
  includeDocs?: boolean; // Include documentation tasks (default: true)
  strategy?: 'balanced' | 'sequential' | 'parallel'; // Split strategy
}

export interface SplitResult {
  originalTask: Task;
  totalTime: number;
  taskCount: number;
  maxDepth: number;
  subtasks: SubTask[];
  criticalPath: string[]; // IDs of tasks on the critical path
}

// Task splitting templates based on task type
const TASK_TEMPLATES: Record<TaskType, (task: string) => SubTask[]> = {
  [TaskType.FEATURE]: (task: string) => [
    { id: '1', title: '需求分析和设计', estimatedTime: 60, type: TaskType.RESEARCH, priority: 'high' },
    { id: '2', title: '数据模型设计', estimatedTime: 90, type: TaskType.FEATURE, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '后端API开发', estimatedTime: 180, type: TaskType.FEATURE, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '前端界面开发', estimatedTime: 180, type: TaskType.FEATURE, priority: 'high', dependencies: ['2'] },
    { id: '5', title: '集成测试', estimatedTime: 120, type: TaskType.TESTING, priority: 'medium', dependencies: ['3', '4'] },
    { id: '6', title: '文档编写', estimatedTime: 60, type: TaskType.DOCUMENTATION, priority: 'low', dependencies: ['5'] }
  ],
  
  [TaskType.BUGFIX]: (task: string) => [
    { id: '1', title: '问题复现', estimatedTime: 30, type: TaskType.RESEARCH, priority: 'high' },
    { id: '2', title: '根因分析', estimatedTime: 60, type: TaskType.RESEARCH, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '修复实现', estimatedTime: 90, type: TaskType.BUGFIX, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '单元测试', estimatedTime: 60, type: TaskType.TESTING, priority: 'high', dependencies: ['3'] },
    { id: '5', title: '回归测试', estimatedTime: 90, type: TaskType.TESTING, priority: 'medium', dependencies: ['4'] }
  ],
  
  [TaskType.REFACTOR]: (task: string) => [
    { id: '1', title: '代码分析', estimatedTime: 60, type: TaskType.RESEARCH, priority: 'high' },
    { id: '2', title: '重构计划', estimatedTime: 60, type: TaskType.DOCUMENTATION, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '重构实施', estimatedTime: 240, type: TaskType.REFACTOR, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '测试验证', estimatedTime: 120, type: TaskType.TESTING, priority: 'high', dependencies: ['3'] },
    { id: '5', title: '性能对比', estimatedTime: 60, type: TaskType.PERFORMANCE, priority: 'medium', dependencies: ['4'] }
  ],
  
  // Default template for other types
  [TaskType.DOCUMENTATION]: (task: string) => [
    { id: '1', title: '内容规划', estimatedTime: 30, type: TaskType.DOCUMENTATION, priority: 'medium' },
    { id: '2', title: '内容编写', estimatedTime: 120, type: TaskType.DOCUMENTATION, priority: 'medium', dependencies: ['1'] },
    { id: '3', title: '审查修订', estimatedTime: 60, type: TaskType.REVIEW, priority: 'low', dependencies: ['2'] }
  ],
  
  [TaskType.TESTING]: (task: string) => [
    { id: '1', title: '测试计划', estimatedTime: 60, type: TaskType.DOCUMENTATION, priority: 'high' },
    { id: '2', title: '测试用例设计', estimatedTime: 90, type: TaskType.TESTING, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '测试执行', estimatedTime: 180, type: TaskType.TESTING, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '缺陷跟踪', estimatedTime: 60, type: TaskType.TESTING, priority: 'medium', dependencies: ['3'] }
  ],
  
  [TaskType.PERFORMANCE]: (task: string) => [
    { id: '1', title: '性能基准测试', estimatedTime: 120, type: TaskType.PERFORMANCE, priority: 'high' },
    { id: '2', title: '瓶颈分析', estimatedTime: 90, type: TaskType.RESEARCH, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '优化实施', estimatedTime: 180, type: TaskType.PERFORMANCE, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '效果验证', estimatedTime: 60, type: TaskType.PERFORMANCE, priority: 'medium', dependencies: ['3'] }
  ],
  
  [TaskType.SECURITY]: (task: string) => [
    { id: '1', title: '安全审计', estimatedTime: 120, type: TaskType.SECURITY, priority: 'high' },
    { id: '2', title: '漏洞修复', estimatedTime: 180, type: TaskType.SECURITY, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '渗透测试', estimatedTime: 120, type: TaskType.TESTING, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '安全加固', estimatedTime: 90, type: TaskType.SECURITY, priority: 'medium', dependencies: ['3'] }
  ],
  
  [TaskType.DEVOPS]: (task: string) => [
    { id: '1', title: '环境准备', estimatedTime: 60, type: TaskType.DEVOPS, priority: 'high' },
    { id: '2', title: 'CI/CD配置', estimatedTime: 120, type: TaskType.DEVOPS, priority: 'high', dependencies: ['1'] },
    { id: '3', title: '部署脚本', estimatedTime: 90, type: TaskType.DEVOPS, priority: 'high', dependencies: ['2'] },
    { id: '4', title: '监控配置', estimatedTime: 60, type: TaskType.DEVOPS, priority: 'medium', dependencies: ['3'] }
  ],
  
  [TaskType.RESEARCH]: (task: string) => [
    { id: '1', title: '资料收集', estimatedTime: 120, type: TaskType.RESEARCH, priority: 'high' },
    { id: '2', title: '方案对比', estimatedTime: 90, type: TaskType.RESEARCH, priority: 'high', dependencies: ['1'] },
    { id: '3', title: 'POC实现', estimatedTime: 180, type: TaskType.FEATURE, priority: 'medium', dependencies: ['2'] },
    { id: '4', title: '评估报告', estimatedTime: 60, type: TaskType.DOCUMENTATION, priority: 'medium', dependencies: ['3'] }
  ],
  
  [TaskType.REVIEW]: (task: string) => [
    { id: '1', title: '代码审查', estimatedTime: 90, type: TaskType.REVIEW, priority: 'high' },
    { id: '2', title: '反馈整理', estimatedTime: 30, type: TaskType.DOCUMENTATION, priority: 'medium', dependencies: ['1'] },
    { id: '3', title: '改进实施', estimatedTime: 120, type: TaskType.FEATURE, priority: 'medium', dependencies: ['2'] }
  ]
};

export class TaskSplitter {
  constructor(
    private analyzer: EnhancedTaskAnalyzer
  ) {}
  
  /**
   * Split a task into subtasks
   */
  async split(task: Task | string, options: SplitOptions = {}): Promise<SplitResult> {
    const {
      maxHours = 4,
      maxDepth = 3,
      includeTests = true,
      includeDocs = true,
      strategy = 'balanced'
    } = options;
    
    // Ensure we have a Task object
    const taskObj = typeof task === 'string' ? {
      id: Date.now().toString(),
      content: task,
      priority: 'medium' as TaskPriority,
      status: 'pending' as const,
      position: 0
    } : task;
    
    // Analyze the task
    const analysis = await this.analyzer.analyze(taskObj);
    const taskType = this.analyzer.identifyTaskType(taskObj);
    const complexity = analysis.complexity || { score: 50, factors: [], estimatedTime: 180 };
    
    // Get base template
    let subtasks = this.getTaskTemplate(taskType, taskObj.content);
    
    // Customize based on task content
    subtasks = this.customizeSubtasks(subtasks, taskObj.content, analysis);
    
    // Apply filters
    if (!includeTests) {
      subtasks = subtasks.filter(st => st.type !== TaskType.TESTING);
    }
    if (!includeDocs) {
      subtasks = subtasks.filter(st => st.type !== TaskType.DOCUMENTATION);
    }
    
    // Split large tasks if needed
    subtasks = this.splitLargeTasks(subtasks, maxHours * 60);
    
    // Apply strategy
    subtasks = this.applyStrategy(subtasks, strategy);
    
    // Calculate critical path
    const criticalPath = this.calculateCriticalPath(subtasks);
    
    // Calculate totals
    const totalTime = subtasks.reduce((sum, st) => sum + st.estimatedTime, 0);
    const maxDepthActual = this.calculateMaxDepth(subtasks);
    
    return {
      originalTask: taskObj,
      totalTime,
      taskCount: subtasks.length,
      maxDepth: maxDepthActual,
      subtasks,
      criticalPath
    };
  }
  
  /**
   * Get task template based on type
   */
  private getTaskTemplate(type: TaskType, taskContent: string): SubTask[] {
    const template = TASK_TEMPLATES[type] || TASK_TEMPLATES[TaskType.FEATURE];
    const subtasks = template(taskContent);
    
    // Customize task titles based on content
    return subtasks.map(st => ({
      ...st,
      title: this.customizeTitle(st.title, taskContent)
    }));
  }
  
  /**
   * Customize subtask title based on main task
   */
  private customizeTitle(template: string, taskContent: string): string {
    // Extract key terms from task content
    const keywords = this.extractKeywords(taskContent);
    
    // Simple replacement strategy
    if (keywords.length > 0 && template.includes('设计')) {
      return template.replace('设计', `${keywords[0]}设计`);
    }
    if (keywords.length > 0 && template.includes('开发')) {
      return template.replace('开发', `${keywords[0]}开发`);
    }
    if (keywords.length > 0 && template.includes('实现')) {
      return template.replace('实现', `${keywords[0]}实现`);
    }
    
    return template;
  }
  
  /**
   * Extract keywords from task content
   */
  private extractKeywords(content: string): string[] {
    const keywords: string[] = [];
    
    // Common patterns
    const patterns = [
      /实现(.+?)功能/,
      /添加(.+?)模块/,
      /创建(.+?)系统/,
      /开发(.+?)服务/,
      /集成(.+?)API/,
      /优化(.+?)性能/,
      /修复(.+?)问题/,
      /重构(.+?)代码/
    ];
    
    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        keywords.push(match[1].trim());
      }
    }
    
    return keywords;
  }
  
  /**
   * Customize subtasks based on analysis
   */
  private customizeSubtasks(
    subtasks: SubTask[],
    taskContent: string,
    analysis: EnhancedAnalysisResult
  ): SubTask[] {
    // Adjust time based on complexity
    const complexityFactor = (analysis.complexity?.score || 50) / 50;
    
    return subtasks.map(st => ({
      ...st,
      estimatedTime: Math.round(st.estimatedTime * complexityFactor)
    }));
  }
  
  /**
   * Split tasks that exceed max hours
   */
  private splitLargeTasks(subtasks: SubTask[], maxMinutes: number): SubTask[] {
    const result: SubTask[] = [];
    
    for (const task of subtasks) {
      if (task.estimatedTime <= maxMinutes) {
        result.push(task);
      } else {
        // Split into smaller chunks
        const chunks = Math.ceil(task.estimatedTime / maxMinutes);
        const chunkTime = Math.floor(task.estimatedTime / chunks);
        
        for (let i = 0; i < chunks; i++) {
          result.push({
            ...task,
            id: `${task.id}-${i + 1}`,
            title: `${task.title} (Part ${i + 1}/${chunks})`,
            estimatedTime: chunkTime,
            dependencies: i === 0 ? task.dependencies : [`${task.id}-${i}`]
          });
        }
      }
    }
    
    return result;
  }
  
  /**
   * Apply splitting strategy
   */
  private applyStrategy(subtasks: SubTask[], strategy: string): SubTask[] {
    switch (strategy) {
      case 'sequential':
        // Make all tasks sequential
        return subtasks.map((task, index) => ({
          ...task,
          dependencies: index === 0 ? [] : [subtasks[index - 1].id]
        }));
        
      case 'parallel':
        // Remove non-critical dependencies
        return subtasks.map(task => ({
          ...task,
          dependencies: task.dependencies?.filter(dep => 
            subtasks.find(st => st.id === dep && st.priority === 'high')?.id
          ) || []
        }));
        
      case 'balanced':
      default:
        // Keep original dependencies
        return subtasks;
    }
  }
  
  /**
   * Calculate critical path
   */
  private calculateCriticalPath(subtasks: SubTask[]): string[] {
    // Build dependency graph
    const graph = new Map<string, string[]>();
    const times = new Map<string, number>();
    
    for (const task of subtasks) {
      graph.set(task.id, task.dependencies || []);
      times.set(task.id, task.estimatedTime);
    }
    
    // Calculate earliest start times
    const earliestStart = new Map<string, number>();
    const visited = new Set<string>();
    
    const calculateEarliestStart = (taskId: string): number => {
      if (visited.has(taskId)) {
        return earliestStart.get(taskId) || 0;
      }
      
      visited.add(taskId);
      const deps = graph.get(taskId) || [];
      
      if (deps.length === 0) {
        earliestStart.set(taskId, 0);
        return 0;
      }
      
      let maxDepTime = 0;
      for (const dep of deps) {
        const depStart = calculateEarliestStart(dep);
        const depTime = times.get(dep) || 0;
        maxDepTime = Math.max(maxDepTime, depStart + depTime);
      }
      
      earliestStart.set(taskId, maxDepTime);
      return maxDepTime;
    };
    
    // Calculate for all tasks
    for (const task of subtasks) {
      calculateEarliestStart(task.id);
    }
    
    // Find the longest path
    let maxEndTime = 0;
    let lastTask = '';
    
    for (const task of subtasks) {
      const start = earliestStart.get(task.id) || 0;
      const duration = times.get(task.id) || 0;
      const endTime = start + duration;
      
      if (endTime > maxEndTime) {
        maxEndTime = endTime;
        lastTask = task.id;
      }
    }
    
    // Trace back the critical path
    const criticalPath: string[] = [];
    let currentTask = lastTask;
    
    while (currentTask) {
      criticalPath.unshift(currentTask);
      const deps = graph.get(currentTask) || [];
      
      // Find the dependency on the critical path
      let nextTask = '';
      let maxTime = -1;
      
      for (const dep of deps) {
        const depStart = earliestStart.get(dep) || 0;
        const depDuration = times.get(dep) || 0;
        const depEnd = depStart + depDuration;
        
        if (depEnd > maxTime) {
          maxTime = depEnd;
          nextTask = dep;
        }
      }
      
      currentTask = nextTask;
    }
    
    return criticalPath;
  }
  
  /**
   * Calculate maximum depth of task tree
   */
  private calculateMaxDepth(subtasks: SubTask[]): number {
    const depthMap = new Map<string, number>();
    
    const calculateDepth = (taskId: string): number => {
      if (depthMap.has(taskId)) {
        return depthMap.get(taskId)!;
      }
      
      const task = subtasks.find(st => st.id === taskId);
      if (!task || !task.dependencies || task.dependencies.length === 0) {
        depthMap.set(taskId, 1);
        return 1;
      }
      
      let maxDepth = 0;
      for (const dep of task.dependencies) {
        maxDepth = Math.max(maxDepth, calculateDepth(dep));
      }
      
      const depth = maxDepth + 1;
      depthMap.set(taskId, depth);
      return depth;
    };
    
    let maxDepth = 0;
    for (const task of subtasks) {
      maxDepth = Math.max(maxDepth, calculateDepth(task.id));
    }
    
    return maxDepth;
  }
}